import CardPlaceholder from '@/components/CardPlaceholder';
import { CardPlaceholderType } from '@/components/CardPlaceholder/types';
import ContentCard from '@/components/ContentCard';
import EmptySection from '@/components/EmptySection';
import GoBackMenu from '@/components/GoBackNavbar';
import InputSearch from '@/components/InputSearch';
import Loader from '@/components/Loader';
import Constants from '@/Constants';
import Category from '@/models/Category';
import { ContentPaginationData } from '@/models/ContentPaginationData';
import { ContentRequest } from '@/models/ContentRequest';
import Item from '@/models/Item';
import { Lesson } from '@/models/Lesson';
import { LessonContentTO } from '@/models/LessonContentTO';
import { LessonData } from '@/models/LessonData';
import Tag from '@/models/Tag';
import { analyticsService, PlaylistEvents } from '@/services/AnalyticsService';
import { contentService } from '@/services/ContentService';
import { ApplicationState } from '@/store';
import { hideHeaderMenu, showHeaderMenu } from '@/store/header/action';
import { clearContentList, contentsSearchedClear, getContentListRequest } from '@/store/item/action';
import { ContentStudentView, ItemTypes } from '@/store/item/types';
import { getCourseRequest } from '@/store/list/action';
import { searchChange, searchClear } from '@/store/search/action';
import { addContentToCourseRequest, getAddedContentListClear, getAddedContentListRequest } from '@/store/student/actions';
import { default as React, RefObject, SyntheticEvent, useCallback, useEffect, useRef, useState } from 'react';
import { useBottomScrollListener } from 'react-bottom-scroll-listener';
import { useDispatch, useSelector } from 'react-redux';
import { Link, useHistory, useParams } from 'react-router-dom';
import _ from 'underscore';
import AddedContent from '../AddedContent';
import './style.scss';

interface LocationState {
    fromContentPreview: boolean;
}

const SuggestedContentPage: React.FC = () => {
    const { courseId } = useParams<{ courseId: string }>();
    const dispatch = useDispatch();
    const history = useHistory();

    const {
        list,
        listTotal,
        strings,
        personId,
        myCurrentCourse,
        loading,
        addedLessons,
        searchedList,
        searchedListTotal,
        searchTerm,
        ownerId,
        availableContentLanguages,
    } = useSelector((state: ApplicationState) => {
        return {
            list: state.itens.listData.listSuggestedStudentView.list,
            listTotal: state.itens.listData.listSuggestedStudentView.total,
            personId: state.identity.data.person.id || 0,
            myCurrentCourse: state.lists.courseData,
            strings: state.locale.data,
            loading: state.itens.loading,
            addedLessons: state.student.myListData.courseStudentView.course.lessons || [],
            searchedList: state.itens.listData.listSearchResultStudentView.list,
            searchedListTotal: state.itens.listData.listSearchResultStudentView.total,
            searchTerm: state.search.data.term,
            ownerId: state.identity.data.person.id,
            availableContentLanguages: state.locale.availableContentLanguages,
        };
    });

    const [searchValue, setSearchValue] = useState<string>('');
    const [firstRequest, setFirstRequest] = useState<boolean>(true);
    const [suggestedList, setSuggestedList] = useState<ContentStudentView[]>([]);
    const [useSearchedResult, setUseSearchedResult] = useState<boolean>(false);
    const [doSearchRequest, setDoSearchRequest] = useState<boolean>(false);
    const [doSuggestedRequest, setDoSuggestedRequest] = useState<boolean>(false);
    const openContentPreviewRef = useRef<boolean>(false);

    const locationState = history.location.state as LocationState;

    const contentLanguage = _.isEmpty(availableContentLanguages) ? myCurrentCourse.locale : availableContentLanguages.map((lang) => lang).join(',');

    const getContentList = (): void => {
        const paginationData: ContentPaginationData = {
            offset: suggestedList.length,
            limit: Constants.COURSE_SECTION_PAGINATION_LIMIT,
            contentRequestSuccessType: ItemTypes.SUGGESTED_SUCCESS,
            doRequest: true,
        } as ContentPaginationData;

        const categoryIds = myCurrentCourse.categories.map((category: Category) => category.id).join(',');
        const tagIds = myCurrentCourse.tags.map((tag: Tag) => tag.id).join(',');

        const requestData: ContentRequest = {
            paginationData,
            personId,
            courseId: myCurrentCourse.id,
            locales: contentLanguage,
            categoryIds,
            tagIds,
            listTypeParm: 'SUGGESTED_CONTENTS_TO_PLAYLIST',
        } as ContentRequest;

        dispatch(getContentListRequest(requestData));
        setFirstRequest(false);
    };

    const getSearchedContentList = (): void => {
        const paginationData: ContentPaginationData = {
            offset: suggestedList.length,
            limit: Constants.COURSE_SECTION_PAGINATION_LIMIT,
            contentRequestSuccessType: ItemTypes.SEARCHED_SUCCESS,
            doRequest: true,
        } as ContentPaginationData;

        const requestData: ContentRequest = {
            paginationData,
            personId,
            courseId: myCurrentCourse.id,
            locales: contentLanguage,
            listTypeParm: 'SEARCH_CONTENTS_TO_PLAYLIST',
            term: searchValue,
        } as ContentRequest;

        dispatch(searchChange(searchValue));
        dispatch(getContentListRequest(requestData));
    };

    const resetSearchAndSuggestedList = () => {
        dispatch(clearContentList('listSuggestedStudentView'));
        dispatch(contentsSearchedClear());
        setSuggestedList([]);
    };

    const submitSearch = () => {
        if (searchValue !== '') {
            resetSearchAndSuggestedList();
            setUseSearchedResult(true);
            setDoSearchRequest(true);
        }
    };

    const availableContentsScrollCallback = useCallback(() => {
        const suggestedListTotal = useSearchedResult ? searchedListTotal : listTotal;

        if (suggestedListTotal < Constants.COURSE_SECTION_PAGINATION_LIMIT) {
            return;
        }

        if (suggestedList.length < suggestedListTotal && !loading) {
            useSearchedResult ? getSearchedContentList() : getContentList();
        }
    }, [suggestedList, loading]);

    const availableContentsScrollRef = useBottomScrollListener(availableContentsScrollCallback, {
        offset: 150,
        triggerOnNoScroll: true,
    }) as RefObject<HTMLDivElement>;

    const getPlaceHolderQuantity = () => {
        const element = document.getElementById('available-list') as HTMLElement;

        if (element) {
            return Math.round(element.clientHeight / 100);
        }

        return 0;
    };

    const addContent = (contentId: number, contentName: string): void => {
        const lessonData: LessonData[] = [
            {
                mediaLesson: {
                    '@class': '.LessonContentTO',
                    id: contentId,
                    course: { id: myCurrentCourse.id },
                    locale: myCurrentCourse.locale,
                } as LessonContentTO,
            },
        ];
        dispatch(addContentToCourseRequest(lessonData, personId, myCurrentCourse.id));
        analyticsService.logEvent(PlaylistEvents.ADD_ITEM_ON_STUDENT_PLAYLIST, {
            entityId: contentId,
            entityName: contentName,
            entityType: Constants.entityTypes.CONTENT,
        });
    };

    const openContentPreview = (e: SyntheticEvent, content: Item) => {
        e.preventDefault();
        openContentPreviewRef.current = true;
        history.replace(`/mine/courses/${myCurrentCourse.id}/contents/${content.id}`);
    };

    useEffect(() => {
        if (doSearchRequest) {
            getSearchedContentList();
            setDoSearchRequest(false);
        }
    }, [doSearchRequest]);

    useEffect(() => {
        if (doSuggestedRequest) {
            getContentList();
            setDoSuggestedRequest(false);
        }
    }, [doSuggestedRequest]);

    useEffect(() => {
        if (searchValue === '' && !firstRequest) {
            resetSearchAndSuggestedList();
            setFirstRequest(true);
            setUseSearchedResult(false);
            setDoSuggestedRequest(true);
            dispatch(searchClear());
        }
    }, [searchValue]);

    useEffect(() => {
        dispatch(hideHeaderMenu());
        if (!locationState || !locationState.fromContentPreview) {
            dispatch(searchClear());
            resetSearchAndSuggestedList();
            dispatch(getCourseRequest(parseInt(courseId), true));
        } else if (searchTerm && searchValue === '') {
            resetSearchAndSuggestedList();
            setUseSearchedResult(true);
            setDoSearchRequest(true);
            setSearchValue(searchTerm);
        }

        return () => {
            if (!openContentPreviewRef.current) {
                dispatch(showHeaderMenu());
                dispatch(searchClear());
            }
            resetSearchAndSuggestedList();
            dispatch(getAddedContentListClear());
        };
    }, []);

    useEffect(() => {
        if (myCurrentCourse.id) {
            if (myCurrentCourse.ownerId !== ownerId) {
                history.replace('/mine/mycreations');
                return;
            }

            getContentList();
            dispatch(getAddedContentListRequest(personId, myCurrentCourse.id, true));
        }
    }, [myCurrentCourse.id]);

    useEffect(() => {
        const currentList = (useSearchedResult ? searchedList : list).filter(
            (csv: ContentStudentView) => !addedLessons.find((lesson: Lesson) => csv.content.id === lesson.content.id),
        );

        setSuggestedList(currentList);
    }, [addedLessons.length, list.length, searchedList.length]);

    const showCardPlaceholder = listTotal === 0 && searchedListTotal === 0 && loading;
    const showEmptyState = listTotal === 0 && searchedListTotal === 0 && !(loading || showCardPlaceholder) && !firstRequest;
    const showSuggestedList = !(showCardPlaceholder || showEmptyState);
    const showLoader = showSuggestedList && loading && listTotal + searchedListTotal > 0;
    const isEditing = Number(localStorage.getItem('myCourseInEdition')) === myCurrentCourse.id;

    if (!myCurrentCourse.id) {
        return null;
    }

    return (
        <div className="main">
            <div className="suggested-content-page">
                <GoBackMenu />
                <div className="suggested-content-page-container">
                    <div className="available-contents">
                        <div className="available-contents-container">
                            <div id="available-list" ref={availableContentsScrollRef}>
                                <h3 className="available-contents-title">
                                    {isEditing ? strings.MANAGE_ITEMS : strings.SUGGESTED_CONTENTS.ADD_CONTENTS}
                                </h3>
                                <p>
                                    {isEditing
                                        ? strings.SUGGESTED_CONTENTS.YOU_CAN_ALSO_ADD_MORE_CONTENTS
                                        : strings.SUGGESTED_CONTENTS.WE_SELECTED_SOME_CONTENTS_FOR_YOU}
                                </p>
                                <InputSearch onSubmit={submitSearch} placeholder={strings.SEARCH} setValue={setSearchValue} value={searchValue} />
                                {showSuggestedList &&
                                    suggestedList.map((contentStudentView: ContentStudentView) => {
                                        const { content, contentEnrollment } = contentStudentView;
                                        return (
                                            <Link
                                                className="link-to-content"
                                                key={content.id}
                                                onClick={(e) => openContentPreview(e, content)}
                                                to={'#'}
                                            >
                                                <ContentCard
                                                    content={content}
                                                    editionConfig={{
                                                        title: strings.ADD,
                                                        handle: () => addContent(content.id || 0, content.name),
                                                    }}
                                                    editionMode
                                                    finished={contentService.getContentStatusIsCompleted(contentEnrollment)}
                                                />
                                            </Link>
                                        );
                                    })}
                                {showCardPlaceholder && (
                                    <CardPlaceholder loading={true} quantity={getPlaceHolderQuantity()} type={CardPlaceholderType.CONTENT_CARD} />
                                )}
                                {showEmptyState && (
                                    <EmptySection
                                        iconName={Constants.icons.SMS_FAILED}
                                        message={useSearchedResult ? strings.NO_RESULTS_FOR_SEARCH : strings.EMPTY_SECTION_MESSAGE}
                                        outlined={false}
                                    />
                                )}
                                {showLoader && <Loader />}
                            </div>
                        </div>
                    </div>
                    <AddedContent />
                </div>
            </div>
        </div>
    );
};

export default SuggestedContentPage;
